/**
 * Signing in with a CET account.
 *
 * The whole integration is a script from the API gateway that installs
 * `window.cet.accessmanagement`, and four of its methods. ProductPlayer's `showCetLoginDialog`
 * is a three-line wrapper around one of them; there is no SDK to adopt and nothing to
 * reimplement.
 *
 * **This code never sees a token, and should not.** The session travels in a `CetState` cookie
 * that is almost certainly `HttpOnly`, so the browser cannot read it — and does not need to:
 * `getSessionAsync()` returns the user directly. Anything that needs to *trust* an identity is
 * server-side, where `AuthHelper` shows the token is an encrypted JWE checked against a Redis
 * logout blacklist. Neither of those is a thing a page can do, and attempting it here would
 * produce an identity that only looks verified.
 *
 * So what this gives the studio and the player is a name to display and a sign-in button. Authorisation for
 * anything that matters happens where the request lands.
 */

/**
 * CET's SSO gateway, derived from our own hostname and NOT configurable.
 *
 * Deriving is right here, unlike the asset origins a page is told by its config. CET names environments as a label inside the domain, so
 * the hostname already says which environment this is — and a *configurable* gateway is how a
 * testing machine ends up authenticating against production, which is the one mistake in this area
 * worth designing out rather than documenting.
 *
 * Always https: the gateway is https-only, and a browser will load an https script into an http
 * page even though the reverse is blocked.
 *
 * `null` anywhere outside cet.ac.il, including localhost — the session cookie is scoped to that
 * domain and would never be sent, so a sign-in control there could only ever fail.
 */
export const SSO_GATEWAY: string | null = (() => {
  const rest = location.hostname.split('.').slice(1).join('.');
  if (!rest.endsWith('cet.ac.il')) return null;

  const env = rest.slice(0, -'cet.ac.il'.length).replace(/\.$/, '');
  return `https://apigateway.${env === '' ? '' : `${env}.`}cet.ac.il`;
})();

/** Only the parts we use. The provider exposes more; naming it all would be inventing a contract. */
interface AccessManagement {
  getSessionAsync(): Promise<unknown>;
  loginCet(redirectUrl: string, skin: string | null, lang: string): void;
  logout(returnUrl: string): void;
  /**
   * Optional because the gateway is versioned independently of this page. It has shipped
   * everywhere we deploy, but a build that predates it must leave the studio working rather than
   * throwing on a timer — the consequence of its absence is the idle logout we have today.
   */
  extendSessionAsync?: () => Promise<boolean>;
}

/** What `environment.js` installs. `prefix` is what `AuthHelper` puts in front of `CetState`. */
interface CetEnvironment {
  readonly name: string;
  readonly defaultDomain: string;
  readonly prefix: string;
}

declare global {
  interface Window {
    cet?: { accessmanagement?: AccessManagement; environment?: CetEnvironment };
  }
}

/**
 * Four fields, deliberately, out of the eighteen `getSessionAsync()` returns.
 *
 * The payload also carries `govId` (a national identity number), `email`, `cellPhone`,
 * `firstName`, `lastName` and `gender`. None of that has any business in this application, and
 * the surest way to keep it out of a log, a database column or an error report is to never put it
 * in an object we pass around. So this is a projection, not a wrapper, and there is no `raw`.
 *
 * `userId` is the only identifier that may ever be stored — it is what `uploadedBy` on a catalogue
 * row means. `displayName` is stored beside it for showing only (a comment's author, a row's
 * `*ByName`): a staff member's name, never a document. Invariant 3 is about learner data, and the
 * same reasoning applies to an administrator's identity documents.
 */
export interface CetSession {
  /** GUID. The only identifier that may be persisted. */
  readonly userId: string;
  readonly displayName: string;
  readonly role: string | null;
  readonly isAdmin: boolean;
}

let loading: Promise<AccessManagement | null> | null = null;

/** Load a script, resolving false instead of throwing — a missing one is a state, not a crash. */
function loadScript(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false; // Order matters here — see `provider()`.
    script.addEventListener('load', () => resolve(true));
    script.addEventListener('error', () => resolve(false));
    document.head.append(script);
  });
}

/**
 * Load the provider once, on demand — `environment.js` first, and that order is load-bearing.
 *
 * `accessmanagement.js` looks for `cet.environment` and, when it is absent, loads
 * `//apigateway.cet.ac.il/environment.js` — hardcoded PRODUCTION. So fetching the provider from
 * the testing gateway and nothing else signs in against production while every URL on screen says
 * testing. Setting the environment first, from the same derived gateway, means that fallback never
 * fires.
 *
 * Both are injected rather than written into `index.html` because the gateway depends on which
 * hostname the page is served from, and that is only known at runtime.
 */
function provider(): Promise<AccessManagement | null> {
  if (loading !== null) return loading;

  loading = (async () => {
    if (SSO_GATEWAY === null) return null;

    const existing = window.cet?.accessmanagement;
    if (existing !== undefined) return existing;

    if (window.cet?.environment === undefined) {
      if (!(await loadScript(`${SSO_GATEWAY}/environment.js`))) return null;
    }
    // Refusing to continue without it rather than letting the provider reach for production.
    if (window.cet?.environment === undefined) return null;

    if (!(await loadScript(`${SSO_GATEWAY}/accessmngapi/provider/accessmanagement.js`))) return null;
    return window.cet?.accessmanagement ?? null;
  })();

  return loading;
}

export function ssoAvailable(): boolean {
  return SSO_GATEWAY !== null;
}

/** Why sign-in is not usable, when it is not. Distinguishes three failures the gate must not blur. */
export type SsoFault = 'no-gateway' | 'no-environment' | 'no-provider';

/**
 * Why sign-in did not produce a session — enough to diagnose, and no more.
 *
 * `keys` is the payload's field NAMES with no values. That is deliberate: the values include a
 * national identity number and an email address, and an earlier version of this rendered the whole
 * object onto the page to work out which fields existed. Names alone answer the only question a
 * developer has here — "did it return something, and shaped how?" — and there is no version of
 * that question worth printing a `govId` to answer.
 */
export async function sessionDiagnostic(): Promise<
  { readonly fault: SsoFault } | { readonly keys: readonly string[] }
> {
  if (SSO_GATEWAY === null) return { fault: 'no-gateway' };

  const am = await provider();
  if (am === null) {
    return { fault: window.cet?.environment === undefined ? 'no-environment' : 'no-provider' };
  }

  try {
    const payload = await am.getSessionAsync();
    if (payload === null || typeof payload !== 'object') return { keys: [] };
    return { keys: Object.keys(payload).sort() };
  } catch (error) {
    return { keys: [`threw: ${error instanceof Error ? error.message : String(error)}`] };
  }
}

/**
 * Who is signed in, or null.
 *
 * The field names here are the ones the provider actually returns, confirmed against a real
 * session rather than guessed. An earlier version guessed — and a wrong guess is
 * indistinguishable from being signed out, which produced a sign-in loop with nothing on screen
 * to explain it.
 *
 * `userIsAdmin` is a boolean the provider supplies directly, so it decides. `role === 'admin'` is
 * kept as a second signal because that is the string `AuthHelper` maps to
 * `UserRole.Administrator` server-side, and two sources agreeing is worth more than either alone.
 */
export async function currentSession(): Promise<CetSession | null> {
  const am = await provider();
  if (am === null) return null;

  let payload: unknown;
  try {
    payload = await am.getSessionAsync();
  } catch {
    return null;
  }
  if (payload === null || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const text = (key: string): string | null => {
    const value = record[key];
    return typeof value === 'string' && value !== '' ? value : null;
  };

  // No identifier means no session: the provider answers for a signed-out visitor too.
  const userId = text('userId');
  if (userId === null) return null;

  const role = text('role');

  // Everything else the payload carries — govId, email, cellPhone, names, gender — is dropped
  // here, on purpose. See the note on CetSession.
  return {
    userId,
    displayName: text('displayName') ?? userId,
    role,
    isAdmin: record['userIsAdmin'] === true || role?.toLowerCase() === 'admin',
  };
}

/**
 * Who the registry should record as having done this.
 *
 * A development convenience with a loud name, and it must stay one. In production the API gateway
 * establishes the session before a request arrives and **overwrites** this header from it — a value
 * a browser page supplies is a value anybody can supply, so nothing here may be mistaken for
 * authorisation. It exists so that `uploadedBy` and `publishedBy` say something true on a laptop.
 */
export async function actorHeader(): Promise<Record<string, string>> {
  const session = await currentSession();
  if (session === null) return {};
  // The name is shown in place of the id, so one that is only the id is not worth sending.
  // URI-encoded: a header carries no Hebrew.
  const name = session.displayName === session.userId ? {} : { 'x-cet-user-name': encodeURIComponent(session.displayName) };
  return { 'x-cet-user': session.userId, ...name };
}

/**
 * Push the idle logout back, because someone is still working.
 *
 * `extendSessionAsync()` POSTs `AccessMngApi/state/extend` on the session cookie, and the provider
 * — not this page — owns every rule around it. Three of those are worth knowing, because each one
 * is a thing this module would otherwise be tempted to reimplement:
 *
 * - **It debounces across tabs**, through a cookie holding the last extend, at one request per ten
 *   minutes with a random jitter so two tabs do not both take the turn. So calling this on a short
 *   timer is not chatty; almost every call is answered locally and never reaches the network.
 * - **It checks `isLoggedInAsync()` first** and does nothing when signed out.
 * - **It logs the user out when the extend is refused** — the session hit CET's absolute maximum,
 *   which no amount of activity may extend past. That is the right behaviour and it is why this
 *   returns a boolean rather than throwing: there is nothing for a caller to recover.
 *
 * Resolves `false` when the provider never loaded, or is old enough not to have the method.
 */
async function extendSession(): Promise<boolean> {
  const am = await provider();
  if (am?.extendSessionAsync === undefined) return false;
  try {
    return (await am.extendSessionAsync()) === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- staying signed in

/**
 * At most one extend per this, however much someone is doing.
 *
 * A scroll is hundreds of events and a session needs extending about as often as a person makes a
 * cup of tea. Well under any CET idle timeout, and the provider throttles again behind it — its
 * own cross-tab debounce means roughly one in six of these reaches the network.
 */
const THROTTLE_MS = 90_000;

/**
 * The actions that count. Intent, not presence: no `mousemove`, because a cat on a desk is not an
 * admin, and every event here involves someone meaning something.
 *
 * `scroll` earns its place despite the throttle making it cheap — reading a long validation report
 * is a genuine way to spend ten minutes here without clicking anything.
 */
const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

let lastExtend = 0;
let lastResult: boolean | null = null;
let listening = false;

declare global {
  interface Window {
    /** See `keepSessionAlive`. Present only where there is a session to keep. */
    studioKeepAlive?: () => Record<string, unknown>;
  }
}

function markActivity(): void {
  const now = Date.now();
  if (now - lastExtend < THROTTLE_MS) return;

  // Stamped before the call, not after it: awaiting first would leave the window open for every
  // event that arrives while the request is in flight, which on a scroll is all of them.
  lastExtend = now;
  void extendSession().then((extended) => {
    lastResult = extended;
  });
}

/**
 * Clicking into the asset frame — the last thing this page sees before the cross-origin boundary
 * swallows everything else, and the start of the one stretch of work that is otherwise invisible.
 *
 * Named rather than inline so it can be removed again; an arrow passed to `addEventListener` is a
 * listener nobody can take off.
 */
function onBlur(): void {
  if (document.activeElement instanceof HTMLIFrameElement) markActivity();
}

/**
 * Keep the session alive for as long as someone is working, and hand back nothing — there is no
 * state to hold and nothing to stop.
 *
 * ## Driven by the action, not by a clock
 *
 * There is no timer. Something a person did is what extends the session, throttled so a scroll
 * cannot fire one per frame, and that is the whole mechanism.
 *
 * The obvious alternative is a timer asking "does the user seem active?", and it is worse in a way
 * worth recording, because it looks tidier. Every version of that question is an inference about a
 * person from the state of the window, and each signal it can reach for is wrong somewhere:
 * `document.hasFocus()` is false while devtools holds the keyboard — so watching the thing work is
 * what stops it working — and false again behind a native dialog, the file picker, or an IME
 * candidate window, all of which sit between an admin and an upload. An event is not evidence
 * about a person. It is the person.
 *
 * It is also the smaller design: no interval left running, no visibility rule, no idle window to
 * tune, and no way for a forgotten tab to hold a session open — with nobody there, no event fires
 * and nothing is extended.
 *
 * ## The known gap: work inside the asset
 *
 * The workbench iframe is cross-origin on purpose ([D10](../../../DECISIONS.md)), so **not one of
 * these events reaches this page** while someone drives an attempt inside it. Clicking *into* the
 * frame is caught, through the blur below, and after that the studio sees nothing until they click
 * back out.
 *
 * The honest fix is not another window-state guess — it is that host↔asset protocol traffic
 * arrives in `session.ts` and is real evidence of a person driving the asset. Wiring those
 * callbacks to `markActivity` is the move when this matters; it is left undone rather than
 * approximated.
 *
 * ## When it runs
 *
 * Armed by the gate at the moment it admits an administrator, and torn down by `signOut()`. That
 * pairing is the point: there is exactly one place in this app that knows a session is good, and
 * exactly one that ends it, so neither the listeners nor the diagnostic can outlive the session
 * they belong to. Idempotent, because "we have a session" is a state the gate may reach more than
 * once. Off a cet.ac.il hostname `ssoAvailable()` is false and nothing is installed at all, so
 * localhost and the e2e suite are untouched.
 */
export function keepSessionAlive(): void {
  if (!ssoAvailable() || listening) return;
  listening = true;

  for (const type of ACTIVITY) {
    // Capture, so a handler that stops propagation cannot hide one — and `scroll` does not bubble
    // from an element at all, so the studio's own scrolling panes are only visible from here.
    window.addEventListener(type, markActivity, { capture: true, passive: true });
  }
  window.addEventListener('blur', onBlur);

  /**
   * `studioKeepAlive()` in the console, because a throttled call does nothing observable and the
   * reasons one might not have happened are all invisible from outside this module.
   *
   * Read-only, and deliberately so: calling it must never be a way to stay signed in.
   */
  window.studioKeepAlive = () => ({
    lastExtend:
      lastExtend === 0 ? 'none yet' : `${Math.round((Date.now() - lastExtend) / 1000)}s ago`,
    lastResult:
      lastResult === null ? 'none yet' : lastResult ? 'extended' : 'suppressed or unavailable',
    throttleSeconds: THROTTLE_MS / 1000,
    nextActionExtends: Date.now() - lastExtend >= THROTTLE_MS,
  });
}

/**
 * Stop, because there is no longer a session to keep.
 *
 * `signOut()` navigates away, so in the ordinary case the listeners die with the page and this
 * looks like ceremony. It is not: `logout()` is the provider's call to make and it can decline to
 * navigate — a network failure, a popup blocked — and what is left then is a signed-out page whose
 * every click still POSTs an extend. The `lastExtend` clock is reset with them, so signing back in
 * as somebody else starts from zero rather than inheriting the previous person's throttle.
 */
function stopKeepingAlive(): void {
  if (!listening) return;
  listening = false;

  for (const type of ACTIVITY) {
    // The same `capture: true` as on the way in — a listener added in the capture phase is a
    // different registration from one added in the bubble phase, and removing the wrong one
    // silently removes nothing.
    window.removeEventListener(type, markActivity, { capture: true });
  }
  window.removeEventListener('blur', onBlur);

  lastExtend = 0;
  lastResult = null;
  delete window.studioKeepAlive;
}

/** Hand over to CET's login, returning here afterwards. `showCetLoginDialog`, minus the analytics. */
export async function signIn(lang = 'he'): Promise<void> {
  const am = await provider();
  am?.loginCet(document.location.href, null, lang);
}

export async function signOut(): Promise<void> {
  const am = await provider();
  // Before the provider is asked to go, not after: `logout()` may navigate immediately, and a
  // teardown queued behind a navigation is a teardown that does not happen.
  stopKeepingAlive();
  // ProductPlayer's `logOut` clears sessionStorage first. The studio keeps its own state there,
  // and leaving one user's panel state for the next is the kind of thing nobody debugs twice.
  window.sessionStorage.clear();
  am?.logout(document.location.href);
}

/**
 * Two initials, which is how an avatar carries a name at 26 pixels.
 *
 * Split on whitespace rather than taking the first two characters, so a Hebrew name gives its
 * first and family initials rather than the first two letters of one word. `Intl.Segmenter` is
 * not reached for: a display name is not a place where grapheme clusters are worth a polyfill,
 * and one character from each of two words is right for every name this tool will see.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = [...(words[0] as string)][0] ?? '';
  const last = words.length > 1 ? ([...(words[words.length - 1] as string)][0] ?? '') : '';
  return `${first}${last}`;
}
